import { NgModule } from '@angular/core';
import { RouterModule, Routes } from '@angular/router';
import { AuthGuard } from '@auth0/auth0-angular';
import { AdminPermissionGuard } from './guards/adminPermission/admin-permission.guard';
import { FeatureFlagsGuard } from './guards/featureFlag/feature-flags.guard';
import { UserLoaderGuard } from './guards/userLoader/user-loader.guard';
import { reactMigrationGuard } from './guards/reactMigration/react-migration.guard';
import { FeatureFlagTypes } from './models/featureFlag/featureFlagsTypes.enum';
import { ApiComponent } from './views/api/api/api.component';
import { ChangelogComponent } from './views/changelog/changelog.component';
import { HygieneComponent } from './views/hygiene/hygiene.component';
import { IssuesComponent } from './views/issues/issues.component';
import { IntegrationsComponent } from './views/integrations/integrations.component';
import { AdaptersComponent } from './views/adapters/adapters.component';
import { ConnectComponent } from './views/connect/connect.component';
import { InventoryComponent } from './views/inventory/inventory.component';
import { LayoutComponent } from './views/layout/layout.component';
import { LoginComponent } from './views/login/login.component';
import { MainDashboardComponent } from './views/main-dashboard/main-dashboard.component';
import { AccountManagementComponent } from './views/management/account-management/account-management.component';
import { PlaybookComponent } from './views/playbook/playbook.component';
import { PlaybooksComponent } from './views/playbooks/playbooks.component';
import { AttackSimulationsComponent } from './views/attack-simulations/attack-simulations.component';
import { PoliciesComponent } from './views/policies/policies.component';
import { PoliciesLandingComponent } from './views/policies-landing/policies-landing.component';
import { AccountPolicyComponent } from './views/account-policy/account-policy.component';
import { PolicyComponent } from './views/policy/policy.component';
import { SupportComponent } from './views/support/support.component';
import { ViolationsComponent } from './views/violations/violations.component';
import { VisibilityDashboardComponent } from './views/visibility-dashboard/visibility-dashboard.component';
import { ErrorLoginComponent } from './views/error-login/error-login.component';
import { NoAccessComponent } from './views/no-access/no-access.component';
import { EnterpriseAiDashboardComponent } from './views/enterprise-ai/enterprise-ai-dashboard/enterprise-ai-dashboard.component';
import { AiFindingsPageComponent } from './views/enterprise-ai/ai-findings-page/ai-findings-page.component';
import { AiActivityPageComponent } from './views/enterprise-ai/ai-activity-page/ai-activity-page.component';
import { AiPreventionPageComponent } from './views/enterprise-ai/ai-prevention-page/ai-prevention-page.component';
import { AiDataInsightsPageComponent } from './views/enterprise-ai/ai-data-insights-page/ai-data-insights-page.component';
import { AiInLineControlPageComponent } from './views/enterprise-ai/ai-in-line-control-page/ai-in-line-control-page.component';
import { DataLayerComponent } from './views/data-layer/data-layer.component';
import { BoundariesComponent } from './views/boundaries/boundaries.component';
import { OauthRedirectComponent } from './components/oauth-redirect/oauth-redirect.component';
/**
 * The routing of the app.
 * Each new route needs to be added here.
 * This module is exported as AppRoutingModule and inside the app.module in imported and added to the imports
 */

/**
 * When adding new route, do not forget to add the following:
 * 1. Add the route to the menu array of the navigation-config.service.ts
 * 2. Add public field with new query param into SupportedQueryParams class
 * 3. Update RoutesQueryParamsFactory.create() method
 */
const routes: Routes = [
  { path: 'login', component: LoginComponent, pathMatch: 'full' },
  { path: 'error-login', component: ErrorLoginComponent, pathMatch: 'full' },
  { path: 'no-access', component: NoAccessComponent, pathMatch: 'full' },
  {
    path: 'app/support',
    component: SupportComponent,
    canActivate: [AuthGuard, UserLoaderGuard],
  },
  {
    path: 'app',
    component: LayoutComponent,
    canActivate: [AuthGuard, UserLoaderGuard],
    children: [
      {
        path: '',
        component: MainDashboardComponent,
        canActivate: [UserLoaderGuard],
        canMatch: [reactMigrationGuard(FeatureFlagTypes.ReactMigrationOverview)],
        pathMatch: 'full',
      },
      // Fallback: when canMatch rejects above (React owns the route),
      // this route loads the component so it can trigger a reload.
      {
        path: '',
        component: MainDashboardComponent,
        pathMatch: 'full',
      },
      {
        path: 'adoption',
        component: VisibilityDashboardComponent,
        canActivate: [FeatureFlagsGuard],
        canMatch: [reactMigrationGuard(FeatureFlagTypes.ReactMigrationAdoption)],
      },
      {
        path: 'ai',
        component: EnterpriseAiDashboardComponent,
        canActivate: [FeatureFlagsGuard],
      },
      {
        path: 'ai-findings',
        component: AiFindingsPageComponent,
        canActivate: [FeatureFlagsGuard],
        canMatch: [reactMigrationGuard(FeatureFlagTypes.ReactMigrationFindings)],
      },
      {
        path: 'ai-activity',
        component: AiActivityPageComponent,
        canActivate: [FeatureFlagsGuard],
        canMatch: [reactMigrationGuard(FeatureFlagTypes.ReactMigrationActivity)],
      },
      {
        path: 'ai-prevention',
        component: AiPreventionPageComponent,
        canActivate: [FeatureFlagsGuard],
      },
      {
        path: 'ai-data-insights',
        component: AiDataInsightsPageComponent,
        canActivate: [FeatureFlagsGuard],
      },
      {
        path: 'ai-in-line-control',
        component: AiInLineControlPageComponent,
        canActivate: [FeatureFlagsGuard],
        canMatch: [reactMigrationGuard(FeatureFlagTypes.ReactMigrationInLineControl)],
      },
      {
        path: 'data-lens',
        component: DataLayerComponent,
        canActivate: [FeatureFlagsGuard],
        canMatch: [reactMigrationGuard(FeatureFlagTypes.ReactMigrationDataLens)],
      },
      {
        path: 'connect',
        component: ConnectComponent,
        canActivate: [UserLoaderGuard, FeatureFlagsGuard],
        canMatch: [reactMigrationGuard(FeatureFlagTypes.ReactMigrationAdapters)],
        children: [
          {
            path: '',
            redirectTo: 'integrations',
            pathMatch: 'full',
          },
          {
            path: 'integrations',
            component: IntegrationsComponent,
          },
          {
            path: 'adapters',
            component: AdaptersComponent,
          },
        ],
      },
      {
        path: 'integrations',
        component: IntegrationsComponent,
        canActivate: [UserLoaderGuard, FeatureFlagsGuard],
      },
      {
        path: 'violations',
        component: ViolationsComponent,
        canActivate: [UserLoaderGuard],
        canMatch: [reactMigrationGuard(FeatureFlagTypes.ReactMigrationViolations)],
      },
      {
        path: 'hygiene',
        component: HygieneComponent,
        canActivate: [UserLoaderGuard],
        canMatch: [reactMigrationGuard(FeatureFlagTypes.ReactMigrationHygiene)],
      },
      {
        path: 'issues',
        component: IssuesComponent,
        canActivate: [FeatureFlagsGuard],
        canMatch: [reactMigrationGuard(FeatureFlagTypes.ReactMigrationIssues)],
      },
      {
        path: 'policies',
        component: PoliciesLandingComponent,
        canActivate: [FeatureFlagsGuard],
        canMatch: [reactMigrationGuard(FeatureFlagTypes.ReactMigrationPolicies)],
        children: [
          {
            path: '',
            redirectTo: 'integrations',
            pathMatch: 'full',
          },
          {
            path: 'account',
            component: AccountPolicyComponent,
          },
          {
            path: 'integrations',
            component: PoliciesComponent,
          },
        ],
      },
      {
        path: 'policies/integrations/:id',
        component: PolicyComponent,
        canActivate: [FeatureFlagsGuard],
        canMatch: [reactMigrationGuard(FeatureFlagTypes.ReactMigrationPolicies)],
      },
      {
        path: 'inventory',
        component: InventoryComponent,
        canMatch: [reactMigrationGuard(FeatureFlagTypes.ReactMigrationInventory)],
      },
      {
        path: 'playbooks',
        component: PlaybooksComponent,
        canActivate: [FeatureFlagsGuard],
        canMatch: [reactMigrationGuard(FeatureFlagTypes.ReactMigrationPlaybooks)],
      },
      {
        path: 'playbooks/new',
        component: PlaybookComponent,
        canActivate: [FeatureFlagsGuard],
        canMatch: [reactMigrationGuard(FeatureFlagTypes.ReactMigrationPlaybooks)],
      },
      {
        path: 'playbooks/:id',
        component: PlaybookComponent,
        canActivate: [FeatureFlagsGuard],
        canMatch: [reactMigrationGuard(FeatureFlagTypes.ReactMigrationPlaybooks)],
      },
      {
        path: 'playbooks/:id/copy',
        component: PlaybookComponent,
        canActivate: [FeatureFlagsGuard],
        canMatch: [reactMigrationGuard(FeatureFlagTypes.ReactMigrationPlaybooks)],
      },
      {
        path: 'playbooks/new/templates/:templateId',
        component: PlaybookComponent,
        canActivate: [FeatureFlagsGuard],
        canMatch: [reactMigrationGuard(FeatureFlagTypes.ReactMigrationPlaybooks)],
      },
      {
        path: 'attack-simulations',
        component: AttackSimulationsComponent,
        canActivate: [FeatureFlagsGuard],
        canMatch: [reactMigrationGuard(FeatureFlagTypes.ReactMigrationRedTeaming)],
      },
      {
        path: 'boundaries',
        component: BoundariesComponent,
        canActivate: [FeatureFlagsGuard],
      },
      {
        path: 'management',
        component: AccountManagementComponent,
        canActivate: [AdminPermissionGuard],
        canMatch: [reactMigrationGuard(FeatureFlagTypes.ReactMigrationSettings)],
      },
    ],
  },
  {
    path: 'app/oauth-authorization-code-redirect',
    component: OauthRedirectComponent,
  },
  {
    path: 'redoc',
    component: ApiComponent,
    canActivate: [AuthGuard, FeatureFlagsGuard],
  },
  {
    path: 'changelog',
    component: ChangelogComponent,
    canActivate: [AuthGuard, FeatureFlagsGuard],
  },
  { path: '**', redirectTo: 'app' },
];

@NgModule({
  imports: [RouterModule.forRoot(routes)],
  exports: [RouterModule],
})
export class AppRoutingModule {}
